import './Form.scss'

import {Controller, useFormContext} from 'react-hook-form'
import {Dropdown, OptionType} from '../ui/Dropdown'
import {Mod, WysiwygType} from '../../types'
import React, {HTMLProps} from 'react'

import {Errors} from './form/Errors'
import {File} from './form/File'
import {Theme} from '../Theme'
import {formatBelgianNationalNumberPartial} from '../../util/formatBelgianNationalNumberPartial'
import {style} from '../../util/style'

const styles = style('form', [
	'group',
	'button',
	'buttons',
	'fields',
	'field',
	'text',
	'label',
	'input',
	'textarea',
	'checkbox',
	'radios',
	'radio',
	'select'
])

type InputAttrs =
	| ({component?: undefined} & HTMLProps<HTMLInputElement>)
	| ({component: 'textarea'} & HTMLProps<HTMLTextAreaElement>)
	| ({component: 'select'} & HTMLProps<HTMLSelectElement>)

type RadioAttrs = {
	type: 'radio'
	selectedOption?: string
	label?: string
	mod?: Mod<'half' | 'maxwidth' | 'error'>
	options?: Array<OptionType>
	required?: boolean
}

type SelectAttrs = {
	type: 'select'
	label?: string
	mod?: Mod<'half' | 'maxwidth' | 'error'>
	options?: Array<OptionType>
	required?: boolean
	selectedOption?: OptionType
	enableFilter?: boolean
}

type FileAttrs = {
	type: 'file'
	filesize?: number // Filesize in Mb
	filetypes?: Array<string> // list of accepted filetypes [.pdf,.png]
	multiple?: boolean
	label?: string
	mod?: Mod<'half' | 'maxwidth' | 'error'>
	required?: boolean
	serverErrors?: Array<string>
}

type CheckboxAtts = {
	amount: number | null
	type: 'checkbox'
	label?: string
	mod?: Mod<'half' | 'maxwidth' | 'error'>
	required?: boolean
}

type TextAttrs = {
	type?:
		| 'number'
		| 'text'
		| 'textarea'
		| 'email'
		| 'tel'
		| 'date'
		| 'url'
		| undefined
	label?: string
	mod?: Mod<'half' | 'maxwidth' | 'error' | 'hidden'>
	required?: boolean
}

export type FormFieldType = Omit<InputAttrs, 'label'> &
	(RadioAttrs | SelectAttrs | FileAttrs | CheckboxAtts | TextAttrs)

type LabelType = HTMLProps<HTMLLabelElement> & {maxwidth?: boolean}

export const FormGroup: React.FC<{title?: boolean; button?: boolean}> = ({
	title,
	button,
	children
}) => (
	<div className={styles.group().mod({title}).mod({button})}>{children}</div>
)

export const FormButton: React.FC<{
	className?: string
	overflow?: boolean
	children
}> = ({className, overflow, children}) => {
	return (
		<div className={styles.button().with(className).mod({overflow})}>
			{children}
		</div>
	)
}

export const FormButtons: React.FC<{
	className?: string
	overflow?: boolean
	children
}> = ({className, overflow, children}) => {
	return (
		<div className={styles.buttons().with(className).mod({overflow})}>
			{children}
		</div>
	)
}

export const FormFields = ({children}) => (
	<div className={styles.fields()}>{children}</div>
)

export const FormField: React.FC<FormFieldType> = ({
	component,
	type = 'text',
	name,
	label,
	mod,
	...props
}) => {
	const labelWithAmount = `${label ?? ''}${
		type === 'checkbox' && (props as CheckboxAtts).amount
			? ` (€ ${(props as CheckboxAtts).amount})`
			: ''
	}`
	return (
		<div className={styles.field().mod(component).mod(type).mod(mod)}>
			<div className={styles.field('row')}>
				{type !== 'checkbox' && (
					<Label
						htmlFor={props.id || name}
						label={labelWithAmount}
						maxwidth={true}
					/>
				)}
				{switchComponent(component, type, name, labelWithAmount, props)}
			</div>
			{type !== 'file' && <Errors name={name} />}
		</div>
	)
}

export function switchComponent(component, type, name, label, props) {
	switch (true) {
		case component === 'textarea':
			return <Textarea name={name} {...props} />
		case type === 'checkbox':
			return <Checkbox type={type} name={name} label={label} {...props} />
		case type === 'radio':
			return <Radios name={name} options={props.options} {...props} />
		case type === 'file':
			return file({name, ...props})
		case component === 'select':
			return <Select name={name} options={props.options} {...props} />
		default:
			return <Input type={type} name={name} {...props} />
	}
}

const file = (props: {
	filesize: number
	filetypes: Array<string>
	multiple: boolean
	name: string
	placeholder: string
	required: boolean
	serverErrors: Array<string>
	value?: any
	onChange?: (file: any) => void
}) => {
	const {control} = useFormContext()
	const {
		filesize,
		filetypes,
		multiple,
		name,
		placeholder,
		required,
		serverErrors,
		value = [],
		onChange: onChangeHandler
	} = props

	return (
		<Controller
			name={name}
			control={control}
			defaultValue={value}
			render={({onChange, value: fileValue}) => (
				<File
					onChange={(val: any) => {
						if (onChangeHandler) onChangeHandler(val)
						onChange(val)
					}}
					value={fileValue}
					filesize={filesize}
					filetypes={filetypes}
					multiple={multiple}
					name={name}
					placeholder={placeholder}
					required={required}
					serverErrors={serverErrors}
				/>
			)}
		/>
	)
}

export const FormText: React.FC<{text?: WysiwygType}> = ({text}) => {
	if (!text) return null

	return (
		<div className={styles.text()}>
			<Theme.Wysiwyg>{text}</Theme.Wysiwyg>
		</div>
	)
}

export const Label: React.FC<LabelType> = ({label, maxwidth, ...props}) =>
	label && (
		<label className={styles.label().mod({maxwidth})} {...props}>
			{label}
		</label>
	)

export const Input: React.FC<HTMLProps<HTMLInputElement>> = ({
	type = 'text',
	name,
	onFocus: onFocusHandler,
	disabled,
	...props
}) => {
	const {register, clearErrors} = useFormContext()

	const browserSupportsDateType = () => {
		const input = document.createElement('input')
		input.setAttribute('type', 'date')
		const notADateValue = 'not-a-date'
		input.setAttribute('value', notADateValue)
		return input.value !== notADateValue
	}

	let inputType = type
	if (type === 'date' && !browserSupportsDateType()) inputType = 'text'
	if (type === 'rrn') {
		inputType = 'text'
		props.onChange = (e) => {
			;(e.target as HTMLInputElement).value = formatBelgianNationalNumberPartial(
				(e.target as HTMLInputElement).value
			)
		}
		props.pattern = '[0-9]{2}.[0-9]{2}.[0-9]{2}-[0-9]{3}.[0-9]{2}'
		props.minLength = 15
		props.maxLength = 15
	}

	return (
		<input
			type={inputType}
			id={props.id || name}
			name={name}
			ref={register}
			disabled={disabled}
			className={styles.input().is({disabled})}
			onFocus={(e) => {
				if (onFocusHandler) onFocusHandler(e)
				clearErrors(name)
			}}
			{...props}
		/>
	)
}

export const Textarea = ({name, ...props}) => {
	const {register} = useFormContext()

	return (
		<textarea
			id={props.id || name}
			name={name}
			ref={register}
			className={styles.textarea()}
			{...props}
		/>
	)
}

export const Checkbox = ({name, label, disabled, ...props}) => {
	return (
		<label className={styles.checkbox().is({disabled})}>
			<Input type="checkbox" name={name} disabled={disabled} {...props} />
			<span className={styles.checkbox('input')} />
			<span
				className={styles.checkbox('text')}
				dangerouslySetInnerHTML={{__html: label}}
			/>
		</label>
	)
}

export const Radios: React.FC<{
	name: string
	options: Array<OptionType>
	selectedOption?: string
	disabled?: boolean
}> = ({name, options, selectedOption, disabled, ...props}) => {
	if (!options || options.length === 0) return null

	return (
		<div className={styles.radios().is({disabled})}>
			{options.map((option, index) => (
				<Radio
					key={option.path}
					amount={option.amount}
					name={name}
					value={option.path}
					label={option.title}
					disabled={disabled}
					id={index === 0 ? name : `${name}-${index}`}
					checked={selectedOption && selectedOption === option.path}
					{...props}
				/>
			))}
		</div>
	)
}

export const Radio = ({amount, name, value, label, disabled, ...props}) => {
	const labelWithAmount = `${label}${amount ? ` (€ ${amount})` : ''}`

	return (
		<label className={styles.radio().is({disabled})}>
			<Input
				type="radio"
				name={name}
				value={value}
				disabled={disabled}
				{...props}
			/>
			<span className={styles.radio('input')} />
			<span
				className={styles.radio('text')}
				dangerouslySetInnerHTML={{__html: labelWithAmount}}
			/>
		</label>
	)
}

export const Select: React.FC<{
	name: string
	options: Array<OptionType>
	selectedOption?: OptionType
	onChange?: (val: string) => void
	enableFilter?: boolean
	mod: Mod<'small'>
}> = ({
	name,
	options,
	selectedOption,
	onChange: onChangeHandler,
	enableFilter = false,
	mod
}) => {
	const {control} = useFormContext()
	if (!options || options.length === 0) return null
	return (
		<div className={styles.select}>
			<Controller
				name={name}
				control={control}
				defaultValue={selectedOption}
				render={({onChange, value}) => (
					<Dropdown
						mod={mod}
						onChange={(val: string) => {
							onChange(val)
							if (onChangeHandler) {
								onChangeHandler(val)
							}
						}}
						enableFilter={enableFilter}
						selected={selectedOption || (value as OptionType)}
						items={options}
						customInputId={name}
					/>
				)}
			/>
		</div>
	)
}
